import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { useCampaignsStore } from '@/stores/campaigns';
import { useGalleryStore } from '@/stores/gallery';
import { useAuthStore } from '@/stores/auth';
import { useRoute } from 'vue-router';
import { useToast } from '@dashhudson/dashing-ui';
import { getCompetitorAndUGCHandles } from '@/app/library/utils';
import { trackBoardMediaAdded } from '@/app/boards/mixpanel';
import enumTypes from '@/app/library/constants';

export const useAddToActionMenuStore = defineStore('addToActionMenu', () => {
  const campaignsStore = useCampaignsStore();
  const galleryStore = useGalleryStore();
  const authStore = useAuthStore();
  const route = useRoute();

  const { addToast } = useToast({
    life: 3000,
  });

  // Maps gallery type to selected galleries
  const selectedGalleriesMap = ref({});
  // Maps gallery type to the summaryGalleries response
  const galleryOptionsMap = ref({});

  // Keeps track of which campaigns have been selected
  const selectedCampaigns = ref([]);
  const campaignOptions = ref([]);

  const campaignOptionsPending = computed(() => campaignsStore.pending.summaryCampaigns);

  const galleryOptionsPending = computed(() => galleryStore.pending.summaryGalleries > 0);

  const selectedOptionsLength = computed(() => {
    const selectedCampaignsLength = selectedCampaigns.value.length;
    const selectedGalleriesLength = Object.values(selectedGalleriesMap.value).reduce(
      (acc, selected) => acc + selected.length,
      0,
    );

    return selectedCampaignsLength + (selectedGalleriesLength || 0);
  });

  function formatOptionForList(option) {
    return {
      value: option,
      label: option.name,
    };
  }

  async function loadCampaignOptions(brandId = authStore.currentBrand?.id) {
    if (campaignOptions.value.length) return;
    await campaignsStore.getSummaryCampaigns({ brandId });
    campaignOptions.value = campaignsStore.summaryCampaigns.map((campaign) =>
      formatOptionForList(campaign),
    );
  }

  async function getAddSelectedMediaToGalleriesPromise(galleryType, selectedMedia) {
    const selectedGalleries = selectedGalleriesMap.value[galleryType];
    const mediaIds = selectedMedia.map(
      (media) =>
        // ad card has media id in media.media.mediaId
        media.id,
    );

    const addMediaRequests = selectedGalleries.map((gallery) =>
      galleryStore.addMediaToGallery({ galleryId: gallery.id, mediaIds }),
    );
    await Promise.all(addMediaRequests);

    // Sending Mixpanel Event
    const otherMedia = getCompetitorAndUGCHandles(selectedMedia);
    // Use the first gallery as the gallery type
    const selectedGalleryType = selectedGalleries[0].galleryType;
    trackBoardMediaAdded({
      mediaIDs: mediaIds,
      galleryType: selectedGalleryType,
      galleryList: selectedGalleries,
      addedFrom: route.name,
      mediaAdded: mediaIds.length,
      fromMultiSelectBar: true,
      ownedMedia: selectedMedia.filter((media) => media.sourceType === enumTypes.OWNED).length,
      ugcMedia: otherMedia.ugc,
      competitorMedia: otherMedia.competitor,
    });
  }

  function getAddSelectedMediaToCampaignsPromise(selectedMedia) {
    const addMediaRequests = selectedCampaigns.value.map((campaign) =>
      campaignsStore.addMediaToCampaign({
        campaign,
        media: selectedMedia,
        page: route.name,
      }),
    );
    return Promise.all(addMediaRequests);
  }

  async function addSelectedMediaToSelections(selectedMedia, callback) {
    const addRequests = [];

    if (selectedCampaigns.value) {
      addRequests.push(getAddSelectedMediaToCampaignsPromise(selectedMedia));
    }

    Object.entries(selectedGalleriesMap.value).forEach(([galleryType, selectedGalleries]) => {
      if (!selectedGalleries.length) return;
      addRequests.push(getAddSelectedMediaToGalleriesPromise(galleryType, selectedMedia));
    });

    await Promise.all(addRequests);
    addToast({ severity: 'success', message: 'Your updates have been saved' });
    callback();
  }

  async function loadGalleryOptions(galleryType, brandId = authStore.currentBrand?.id) {
    if (galleryOptionsMap.value[galleryType]?.length) {
      return;
    }
    const galleries = await galleryStore.getSummaryGalleries({ type: galleryType, brandId });
    galleryOptionsMap.value[galleryType] = galleries.map((gallery) => formatOptionForList(gallery));
  }

  function reset() {
    selectedGalleriesMap.value = {};
    galleryOptionsMap.value = {};
    selectedCampaigns.value = [];
    campaignOptions.value = [];
  }

  return {
    reset,
    selectedCampaigns,
    campaignOptions,
    campaignOptionsPending,
    selectedGalleriesMap,
    galleryOptionsMap,
    galleryOptionsPending,
    selectedOptionsLength,
    loadGalleryOptions,
    loadCampaignOptions,
    formatOptionForList,
    addSelectedMediaToSelections,
  };
});
